import { CalendarClock, UserMinus, CalendarX, Check, RotateCcw } from "lucide-react";
import { Reveal, SectionHead, Avatar } from "../components/bits";

const CardGraphic = ({ type }) => {
    if (type === "return")
        return (
            <div className="relative flex h-32 items-center justify-center gap-4 rounded-xl border border-linen bg-ivory">
                <div className="flex flex-col items-center gap-1.5">
                    <Avatar initials="S" tone="bg-blush text-ink" />
                    <span className="font-mono text-[7px] uppercase tracking-widest text-faint">Client</span>
                </div>
                <svg width="52" height="24" fill="none">
                    <path d="M2 12h44m0 0l-6-6m6 6l-6 6" stroke="#D8A7A1" strokeWidth="1.4" className="line-flow" strokeDasharray="4 5" />
                </svg>
                <div className="rounded-lg border border-rose/50 bg-blush/60 p-2">
                    <CalendarClock className="h-5 w-5 text-ink/70" strokeWidth={1.5} />
                </div>
                <span className="absolute bottom-2.5 font-mono text-[7px] uppercase tracking-[0.2em] text-gold">Past usual cycle → return due</span>
            </div>
        );
    if (type === "lapsed")
        return (
            <div className="relative flex h-32 items-center justify-center gap-2 rounded-xl border border-linen bg-ivory">
                {[0.95, 0.6, 0.3].map((o, i) => (
                    <div key={i} className="flex flex-col items-center gap-1" style={{ opacity: o }}>
                        <Avatar initials={["E", "P", "A"][i]} tone={i === 0 ? "bg-blush text-ink" : "bg-sand text-mute"} />
                        <span className="h-1 w-6 rounded-full bg-linen" />
                    </div>
                ))}
                <UserMinus className="absolute right-4 top-4 h-4 w-4 text-rose" strokeWidth={1.5} />
                <span className="absolute bottom-2.5 font-mono text-[7px] uppercase tracking-[0.2em] text-gold">Fading from the database</span>
            </div>
        );
    return (
        <div className="relative flex h-32 items-center justify-center gap-3 rounded-xl border border-linen bg-ivory">
            <div className="flex flex-col items-center gap-1 rounded-lg border border-linen bg-white p-2">
                <CalendarX className="h-4 w-4 text-faint" strokeWidth={1.5} />
                <span className="font-mono text-[7px] uppercase tracking-widest text-faint line-through">Cancelled</span>
            </div>
            <svg width="34" height="20" fill="none">
                <path d="M2 10h26m0 0l-5-5m5 5l-5 5" stroke="#D8A7A1" strokeWidth="1.4" />
            </svg>
            <div className="flex flex-col items-center gap-1 rounded-lg border border-rose/50 bg-blush/60 p-2">
                <RotateCcw className="h-4 w-4 text-ink/70" strokeWidth={1.5} />
                <span className="font-mono text-[7px] uppercase tracking-widest text-ink/70">Reactivate</span>
            </div>
            <span className="absolute bottom-2.5 font-mono text-[7px] uppercase tracking-[0.2em] text-gold">Empty slot → opportunity</span>
        </div>
    );
};

const CARDS = [
    {
        type: "return",
        title: "Due to Return",
        copy: "Clients who are past their usual treatment or booking cycle.",
        testId: "problem-card-return",
    },
    {
        type: "lapsed",
        title: "Lapsed Clients",
        copy: "Past clients who haven’t visited in an agreed period.",
        testId: "problem-card-lapsed",
    },
    {
        type: "never",
        title: "Never Rebooked",
        copy: "Clients who cancelled, no-showed or simply never booked their next appointment.",
        testId: "problem-card-never",
    },
];

export const Problem = () => (
    <section data-testid="problem-section" className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-[1fr_1.6fr] lg:gap-16">
                <div>
                    <SectionHead
                        eyebrow="The problem"
                        title={<>You might not need more leads yet.</>}
                        copy="Most established clinics already have hundreds — sometimes thousands — of people in their database who have visited before but haven’t booked again."
                    />
                    <Reveal delay={0.2} className="mt-8 hidden lg:block">
                        <div className="rounded-2xl border border-linen bg-ivory p-6">
                            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Already in your database</div>
                            <div className="mt-4 space-y-2.5">
                                {["Consulted last year", "Treatment finished", "Follow-up never sent"].map((t, i) => (
                                    <div key={t} className="flex items-center gap-3">
                                        <span className="font-mono text-[9px] text-faint">{String(i + 1).padStart(2, "0")}</span>
                                        <span className="h-1.5 w-1.5 rounded-full bg-rose" />
                                        <span className="font-sans text-[13px] text-mute">{t}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                </div>
                <div className="grid gap-5 sm:grid-cols-3">
                    {CARDS.map((c, i) => (
                        <Reveal key={c.type} delay={i * 0.12}>
                            <article data-testid={c.testId} className="card-lift h-full rounded-2xl border border-linen bg-ivory p-5">
                                <CardGraphic type={c.type} />
                                <div className="mt-5 flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.22em] text-gold">
                                    <span>{String(i + 1).padStart(2, "0")}</span>
                                    <span className="h-px flex-1 bg-linen" />
                                </div>
                                <h3 className="mt-2 font-serif text-xl uppercase tracking-tight text-ink">{c.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-mute">{c.copy}</p>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </div>
            <Reveal delay={0.15} className="mt-14">
                <div className="flex flex-col items-center justify-between gap-5 rounded-2xl bg-ink px-8 py-7 sm:flex-row">
                    <p className="text-center font-serif text-xl uppercase tracking-tight text-cream sm:text-left sm:text-2xl">
                        They’re already in the database.{" "}
                        <span className="italic text-rose">We help get them back onto the calendar.</span>
                    </p>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gold/40">
                        <Check className="h-4 w-4 text-gold" strokeWidth={2} />
                    </span>
                </div>
            </Reveal>
        </div>
    </section>
);
